/**
 * MultiSelectSearchComponent
 * Searchable multi-select with optional filtering
 */

import React, { useCallback, useMemo, useState, memo } from 'react';
import type { FormControlProps } from '../../types';

interface SelectOption {
  id: string;
  label: string;
}

interface MultiSelectSearchComponentProps extends FormControlProps {
  options?: SelectOption[];
  searchable?: boolean;
  showFilters?: boolean;
  filterFields?: string[];
  maxSelections?: number;
  allowCustom?: boolean;
  asyncSearch?: boolean;
  searchEndpoint?: string;
  onChange?: (selectedIds: string[]) => void;
}

export const MultiSelectSearchComponent: React.FC<MultiSelectSearchComponentProps> = memo(({
  value = [],
  options = [],
  searchable = true,
  showFilters = true,
  filterFields = [],
  maxSelections = 0,
  allowCustom = false,
  asyncSearch = false,
  searchEndpoint = '',
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const inputId = useMemo(
    () => `multi-select-search-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const selectedIds = useMemo(() => {
    return new Set(Array.isArray(value) ? (value as string[]) : []);
  }, [value]);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const filteredOptions = useMemo(() => {
    let filtered = options;

    if (searchQuery && searchable) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((opt) =>
        opt.label.toLowerCase().includes(q) || opt.id.toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [options, searchQuery, searchable]);

  const canAddMore = maxSelections === 0 || selectedIds.size < maxSelections;

  const handleToggleSelection = useCallback(
    (optionId: string) => {
      if (!canAddMore && !selectedIds.has(optionId)) {
        return;
      }

      const newSelected = new Set(selectedIds);
      if (newSelected.has(optionId)) {
        newSelected.delete(optionId);
      } else {
        newSelected.add(optionId);
      }

      onChange?.(Array.from(newSelected));
    },
    [selectedIds, canAddMore, onChange],
  );

  const handleRemoveTag = useCallback(
    (optionId: string) => {
      const newSelected = new Set(selectedIds);
      newSelected.delete(optionId);
      onChange?.(Array.from(newSelected));
    },
    [selectedIds, onChange],
  );

  return (
    <div className="af-form-field__group multi-select-search-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="multi-select-search__container">
        {searchable && (
          <input
            type="text"
            placeholder="Search options..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={disabled}
            className="multi-select-search__search-input"
            aria-label="Search options"
          />
        )}

        {selectedIds.size > 0 && (
          <div className="multi-select-search__tags">
            {Array.from(selectedIds).map((id) => {
              const option = options.find((opt) => opt.id === id);
              return (
                <span key={id} className="multi-select-search__tag">
                  {option?.label || id}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(id)}
                    className="multi-select-search__tag-remove"
                    aria-label={`Remove ${option?.label}`}
                    disabled={disabled}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {showFilters && filterFields.length > 0 && (
          <div className="multi-select-search__filters">
            {filterFields.map((field) => (
              <select
                key={field}
                value={activeFilters[field] || ''}
                onChange={(e) =>
                  setActiveFilters({ ...activeFilters, [field]: e.target.value })
                }
                className="multi-select-search__filter-select"
                disabled={disabled}
              >
                <option value="">All {field}</option>
                {/* Filter options would be dynamically populated */}
              </select>
            ))}
          </div>
        )}

        <div className="multi-select-search__options">
          {filteredOptions.length === 0 && (
            <p className="multi-select-search__empty">No options found</p>
          )}
          {filteredOptions.map((option) => {
            const isSelected = selectedIds.has(option.id);
            const isDisabledOption = !canAddMore && !isSelected;

            return (
              <label
                key={option.id}
                className={`multi-select-search__option ${
                  isSelected ? 'multi-select-search__option--selected' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleSelection(option.id)}
                  disabled={disabled || isDisabledOption}
                  className="multi-select-search__checkbox"
                  aria-label={option.label}
                />
                <span className="multi-select-search__option-label">{option.label}</span>
              </label>
            );
          })}
        </div>

        {maxSelections > 0 && (
          <p className="multi-select-search__limit">
            Selected: {selectedIds.size} / {maxSelections}
          </p>
        )}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
